import { cn } from '@/lib/utils';
import instagramLogo from '@/assets/instagram-logo.png';
import tiktokLogo from '@/assets/tiktok-logo.png';
import pinterestLogo from '@/assets/pinterest-logo.png';
import youtubeLogo from '@/assets/youtube-logo.png';

interface SocialPlatformBadgeProps {
  platform: string;
  size?: 'sm' | 'md';
  className?: string;
}

// Map platform names to their brand colors and icons
const platformConfig: Record<string, { bg: string; icon: string | null; label: string; logo?: string }> = {
  instagram: {
    bg: 'bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#F77737]',
    icon: null,
    label: 'Instagram',
    logo: instagramLogo,
  },
  tiktok: {
    bg: 'bg-black',
    icon: null,
    label: 'TikTok',
    logo: tiktokLogo,
  },
  pinterest: {
    bg: 'bg-[#E60023]',
    icon: null,
    label: 'Pinterest',
    logo: pinterestLogo,
  },
  youtube: {
    bg: 'bg-[#FF0000]',
    icon: null,
    label: 'YouTube',
    logo: youtubeLogo,
  },
  twitter: {
    bg: 'bg-black',
    icon: '𝕏',
    label: 'X',
  },
  facebook: {
    bg: 'bg-[#1877F2]',
    icon: 'f',
    label: 'Facebook',
  },
};

export function getPlatformFromUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  
  const lowercaseUrl = url.toLowerCase();
  
  if (lowercaseUrl.includes('instagram.com') || lowercaseUrl.includes('instagr.am')) {
    return 'instagram';
  }
  if (lowercaseUrl.includes('tiktok.com') || lowercaseUrl.includes('tiktok')) {
    return 'tiktok';
  }
  if (lowercaseUrl.includes('pinterest.com') || lowercaseUrl.includes('pin.it')) {
    return 'pinterest';
  }
  if (lowercaseUrl.includes('youtube.com') || lowercaseUrl.includes('youtu.be')) {
    return 'youtube';
  }
  if (lowercaseUrl.includes('twitter.com') || lowercaseUrl.includes('x.com')) {
    return 'twitter';
  }
  if (lowercaseUrl.includes('facebook.com') || lowercaseUrl.includes('fb.com')) {
    return 'facebook';
  }
  
  return null;
}

export function SocialPlatformBadge({ platform, size = 'sm', className }: SocialPlatformBadgeProps) {
  const config = platformConfig[platform];
  
  if (!config) return null;
  
  const sizeClasses = {
    sm: 'w-5 h-5 text-[10px]',
    md: 'w-6 h-6 text-xs',
  };
  
  // Use logo image for platforms with logos
  if (config.logo) {
    return (
      <img 
        src={config.logo}
        alt={config.label}
        className={cn(
          'rounded-full object-cover',
          size === 'sm' ? 'w-5 h-5' : 'w-6 h-6',
          className
        )}
        title={config.label}
      />
    );
  }
  
  return (
    <span 
      className={cn(
        'rounded-full flex items-center justify-center text-white font-bold shadow-sm',
        config.bg,
        sizeClasses[size],
        className
      )}
      title={config.label}
    >
      {config.icon}
    </span>
  );
}

export function SocialPlatformPill({ platform, className }: { platform: string; className?: string }) {
  const config = platformConfig[platform];
  
  if (!config) return null;
  
  return (
    <span 
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-white',
        config.bg,
        className
      )}
    >
      {config.logo ? (
        <img src={config.logo} alt={config.label} className="w-4 h-4 rounded-full" />
      ) : (
        <span>{config.icon}</span>
      )}
      <span>{config.label}</span>
    </span>
  );
}

export { platformConfig };
